import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { PieChart, DollarSign, Zap, MapPin, Shield, Handshake, BatteryCharging } from "lucide-react";

const MarketTab: React.FC = () => {
    return (
        <div className="px-4 space-y-6 mt-6 pb-24">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Market Potential */}
                <Card className="p-6 border border-border shadow-sm flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-4">
                        <PieChart className="text-primary w-5 h-5" />
                        <h3 className="font-bold text-lg">Market Potential</h3>
                    </div>
                    <div className="flex-grow flex flex-col justify-center items-center py-4 relative">
                        {/* Simple CSS-based donut chart representation */}
                        <div
                            className="relative w-32 h-32 rounded-full flex items-center justify-center"
                            style={{ background: "conic-gradient(#6366f1 0% 65%, #e2e8f0 65% 100%)" }}
                        >
                            <div className="absolute inset-4 bg-card rounded-full flex items-center justify-center flex-col">
                                <span className="text-2xl font-black">65%</span>
                                <span className="text-[9px] uppercase font-bold text-muted-foreground">Share</span>
                            </div>
                        </div>
                        <div className="mt-4 text-center">
                            <p className="text-xs font-medium text-muted-foreground">Total Addressable Market</p>
                            <p className="text-lg font-bold">$12.4 Billion</p>
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            The renewable energy sector is projected to grow at a <span className="font-bold text-primary">CAGR of 8.4%</span> over the next decade.
                        </p>
                    </div>
                </Card>

                {/* Monetization */}
                <Card className="p-6 border border-border shadow-sm flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-6">
                        <DollarSign className="text-emerald-500 w-5 h-5" />
                        <h3 className="font-bold text-lg">Monetization</h3>
                    </div>
                    <div className="space-y-4 flex-grow">
                        {[
                            { icon: Zap, color: "text-blue-600", bg: "bg-blue-100", title: "Direct Grid Sales", desc: "Fixed rate PPA contracts" },
                            { icon: Shield, color: "text-emerald-600", bg: "bg-emerald-100", title: "Carbon Credits", desc: "Tradable REC certificates" },
                            { icon: BatteryCharging, color: "text-orange-600", bg: "bg-orange-100", title: "Storage Arbitrage", desc: "Peak demand supply" },
                            { icon: Handshake, color: "text-purple-600", bg: "bg-purple-100", title: "Gov. Incentives", desc: "Tax equity partnerships" }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-lg ${item.bg} dark:bg-opacity-20 flex items-center justify-center shrink-0`}>
                                    <item.icon className={`${item.color} w-4 h-4`} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-sm">{item.title}</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Why Invest Card */}
            <Card className="p-6 border border-border shadow-sm">
                <h2 className="text-xl font-extrabold mb-6">Why Invest in Quantum Solar?</h2>
                <div className="space-y-6">
                    {[
                        {
                            icon: Zap,
                            color: "text-primary",
                            bg: "bg-primary/10",
                            title: "State-of-the-Art Technology",
                            desc: "Featuring the latest bifacial panel modules that capture sunlight from both sides, increasing energy yield by up to 30%."
                        },
                        {
                            icon: MapPin,
                            color: "text-emerald-600",
                            bg: "bg-emerald-50 dark:bg-emerald-900/20",
                            title: "Strategic Location",
                            desc: "Situated in high sunshine region with over 320 days of sun annually and direct proximity to a major industrial substation."
                        },
                        {
                            icon: Shield,
                            color: "text-blue-600",
                            bg: "bg-blue-50 dark:bg-blue-900/20",
                            title: "Secured Revenue",
                            desc: "A 20-year Power Purchase Agreement (PPA) is already signed, guaranteeing a fixed purchase price for 80% of capacity."
                        }
                    ].map((item, idx) => (
                        <div key={idx} className="flex gap-4">
                            <div className="shrink-0 mt-1">
                                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center border border-border`}>
                                    <item.icon className={`${item.color} w-5 h-5`} />
                                </div>
                            </div>
                            <div>
                                <h3 className="font-bold text-base">{item.title}</h3>
                                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
};

export default MarketTab;
